import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, Plus, Users, ChevronDown, Search, AlertTriangle, Clock } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCachedData } from "../lib/useCachedData";
import { invalidate } from "../lib/dataCache";
import { CATEGORY_BY_KEY, DPI_SUBCATEGORIES, TOOL_SUBCATEGORIES } from "../lib/categories";
import { StatusBadge } from "../components/dpi";
import AuthedImage from "../components/AuthedImage";
import DeviceDialog from "../components/DeviceDialog";
import ScaffoldDialog from "../components/ScaffoldDialog";

/*
 * CategoryListPage — unified admin-side list grouped BY CLIENT, then by
 * sub-category. Two-step flow:
 *   /{hub}             → list of clients with counts (admin only). Clients
 *                        see their own list directly (step skipped).
 *   /{hub}/clienti/:id → that client's items, grouped by sub-category for
 *                        kind='dpi'|'strumento', flat list for scaffolds and
 *                        linee vita.
 *
 * Called from App.js like <CategoryListPage categoryKey="dpi" />.
 */

const DEVICE_KINDS = new Set(["dpi", "strumento"]);

function getCategoryLabels(kind) {
  if (kind === "dpi") return DPI_SUBCATEGORIES;
  if (kind === "strumento") return TOOL_SUBCATEGORIES;
  return [];
}

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    const d = iso.includes("T") ? iso.slice(0, 10) : iso;
    const [y, m, da] = d.split("-");
    return `${da}/${m}/${y}`;
  } catch { return iso; }
}

function ExpiryWidget({ cat, items }) {
  const expired = items.filter((i) => i.status === "scaduto").length;
  const expiring = items.filter((i) => i.status === "in_scadenza").length;
  const total = expired + expiring;
  if (total === 0) {
    return (
      <Link
        to={`/scadenze?kind=${cat.kind}`}
        className="block bru-card p-4 mb-5 hover:-translate-y-0.5 hover:shadow-lg transition-all"
        data-testid={`cat-widget-${cat.key}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-neutral-900 text-white">
            <Clock className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="tag-label">Scadenze</div>
            <div className="font-semibold">Tutto in regola per questa categoria</div>
          </div>
          <ChevronRight className="w-5 h-5 text-neutral-400" />
        </div>
      </Link>
    );
  }
  return (
    <Link
      to={`/scadenze?kind=${cat.kind}`}
      className={`block bru-card p-4 mb-5 hover:-translate-y-0.5 hover:shadow-lg transition-all ${
        expired > 0 ? "border-[#FF3B30] bg-[#FFF5F4]" : "border-[#FFCC00] bg-[#FFF9E6]"
      }`}
      data-testid={`cat-widget-${cat.key}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          expired > 0 ? "bg-[#FF3B30] text-white" : "bg-[#FFCC00] text-black"
        }`}>
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="tag-label">Scadenze</div>
          <div className="font-semibold truncate">
            {expired > 0 && <span className="text-[#FF3B30]">{expired} scaduti</span>}
            {expired > 0 && expiring > 0 && <span className="text-neutral-400"> · </span>}
            {expiring > 0 && <span className="text-[#B37400]">{expiring} in scadenza</span>}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-neutral-400" />
      </div>
    </Link>
  );
}

function ClientCards({ cat, items, clients, onNew }) {
  const [search, setSearch] = useState("");
  // Index clients by id for logo lookup (admin fetches /api/clients separately).
  const clientById = useMemo(() => {
    const m = {};
    for (const c of (clients || [])) m[c.id] = c;
    return m;
  }, [clients]);

  // Group counts per client
  const byClient = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const cid = it.client_id;
      if (!cid) continue;
      const entry = map.get(cid) || { client_id: cid, client_name: it.client_name, count: 0, expired: 0, expiring: 0 };
      entry.count += 1;
      if (it.status === "scaduto") entry.expired += 1;
      if (it.status === "in_scadenza") entry.expiring += 1;
      map.set(cid, entry);
    }
    return Array.from(map.values()).sort((a, b) => (a.client_name || "").localeCompare(b.client_name || ""));
  }, [items]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? byClient.filter((c) => (c.client_name || "").toLowerCase().includes(q))
    : byClient;

  return (
    <>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="tag-label">Elenco clienti</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">{cat.plural}</h1>
          <div className="text-sm text-neutral-500 mt-1">
            {byClient.length} {byClient.length === 1 ? "cliente" : "clienti"} · {items.length} {cat.plural.toLowerCase()}
          </div>
        </div>
        <button className="bru-btn bru-btn-primary" onClick={onNew} data-testid={`new-${cat.key}-top`}>
          <Plus className="w-4 h-4" />
          Nuovo {cat.singular}
        </button>
      </div>

      {/* Expiry summary widget */}
      <ExpiryWidget cat={cat} items={items} />

      {/* Search bar */}
      <div className="relative mb-4" data-testid={`search-clients-wrap`}>
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca cliente..."
          className="bru-input !pl-9"
          data-testid="search-clients-input"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="bru-card p-8 text-center text-neutral-500">
          {q ? `Nessun cliente trovato per "${search}".` : `Nessun ${cat.singular.toLowerCase()} registrato.`}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => {
            const cli = clientById[c.client_id];
            const logo = cli?.logo;
            return (
              <Link
                key={c.client_id}
                to={`${cat.hubPath}/clienti/${c.client_id}`}
                className="bru-card p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all"
                data-testid={`client-card-${c.client_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    {logo ? (
                      <AuthedImage
                        path={logo}
                        thumb={128}
                        alt=""
                        className="w-12 h-12 rounded-xl object-contain bg-neutral-50 p-1 shrink-0 border border-neutral-200"
                        fallback={
                          <div className="w-12 h-12 rounded-xl bg-neutral-100 flex items-center justify-center shrink-0">
                            <Users className="w-5 h-5 text-neutral-400" />
                          </div>
                        }
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-neutral-100 flex items-center justify-center shrink-0">
                        <Users className="w-5 h-5 text-neutral-400" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold truncate">{c.client_name || "—"}</h3>
                      <div className="text-xs text-neutral-500 mt-1">
                        {c.count} {c.count === 1 ? cat.singular.toLowerCase() : cat.plural.toLowerCase()}
                      </div>
                      {(c.expired > 0 || c.expiring > 0) && (
                        <div className="mt-2 flex gap-1.5 flex-wrap">
                          {c.expired > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FF3B30] text-white">
                              {c.expired} scaduti
                            </span>
                          )}
                          {c.expiring > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FFCC00] text-black">
                              {c.expiring} in scadenza
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-neutral-400 shrink-0" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}

function ClientItemsView({ cat, clientId, items, clients, onNew }) {
  const [search, setSearch] = useState("");
  const client = (clients || []).find((c) => c.id === clientId) || items.find((i) => i.client_id === clientId);
  const clientName = client?.name || client?.client_name || "Cliente";
  const clientLogo = client?.logo;
  const own = items.filter((i) => i.client_id === clientId);
  const isDeviceKind = DEVICE_KINDS.has(cat.kind);
  const subcats = getCategoryLabels(cat.kind);
  const labelByValue = Object.fromEntries(subcats.map((s) => [s.value, s.label]));
  const [openCats, setOpenCats] = useState(() => new Set(subcats.map((s) => s.value)));

  const q = search.trim().toLowerCase();
  const matchItem = (it) => {
    if (!q) return true;
    const hay = [
      it.code, it.reference, it.brand, it.model, it.serial_number,
      it.dipendente, it.address, it.notes,
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  };
  const filteredOwn = own.filter(matchItem);

  const grouped = useMemo(() => {
    if (!isDeviceKind) return { "_all": filteredOwn };
    const map = {};
    for (const it of filteredOwn) {
      const key = it.category || "altro";
      (map[key] = map[key] || []).push(it);
    }
    return map;
  }, [filteredOwn, isDeviceKind]);

  const toggleCat = (k) => {
    setOpenCats((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };

  const Row = ({ it }) => {
    const detailPath = cat.detailPath(it.id);
    const latest = it.latest_inspection || it.latest_revision;
    const expDate = latest?.expiration_date || it.next_inspection_date;
    return (
      <Link
        to={detailPath}
        className="flex items-center justify-between gap-3 py-3 px-4 hover:bg-[#fff7f0] border-b border-neutral-200 last:border-b-0"
        data-testid={`client-item-${it.id}`}
      >
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">
            {it.code || it.reference}
            {it.brand && <span className="text-neutral-500 font-normal ml-2">· {it.brand}</span>}
            {it.model && <span className="text-neutral-500 font-normal"> {it.model}</span>}
          </div>
          <div className="text-xs text-neutral-500 truncate mt-0.5">
            {it.serial_number && <>SN {it.serial_number} · </>}
            {it.dipendente && <>{it.dipendente} · </>}
            {it.address && <>{it.address}</>}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right text-xs text-neutral-500 tabular-nums hidden sm:block">
            {expDate ? `Scad. ${fmtDate(expDate)}` : "—"}
          </div>
          <StatusBadge status={it.status} />
          <ChevronRight className="w-4 h-4 text-neutral-400" />
        </div>
      </Link>
    );
  };

  return (
    <>
      <Link to={cat.hubPath} className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 mb-4" data-testid="back-to-clients">
        <ArrowLeft className="w-4 h-4" /> Tutti i clienti
      </Link>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-4 min-w-0">
          {clientLogo ? (
            <AuthedImage
              path={clientLogo}
              thumb={192}
              alt=""
              className="w-16 h-16 rounded-2xl object-contain bg-neutral-50 p-1.5 shrink-0 border border-neutral-200"
              fallback={null}
            />
          ) : null}
          <div className="min-w-0">
            <div className="tag-label">{cat.plural}</div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1 truncate">{clientName}</h1>
            <div className="text-sm text-neutral-500 mt-1">
              {own.length} {own.length === 1 ? cat.singular.toLowerCase() : cat.plural.toLowerCase()}
            </div>
          </div>
        </div>
        <button className="bru-btn bru-btn-primary" onClick={() => onNew(clientId)} data-testid={`new-${cat.key}-client`}>
          <Plus className="w-4 h-4" />
          Nuovo
        </button>
      </div>

      {/* Expiry summary widget — filtered to this client */}
      <ExpiryWidget cat={cat} items={own} />

      {/* Search bar */}
      <div className="relative mb-4" data-testid="search-items-wrap">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Cerca ${cat.plural.toLowerCase()}...`}
          className="bru-input !pl-9"
          data-testid="search-items-input"
        />
      </div>

      {own.length === 0 ? (
        <div className="bru-card p-8 text-center text-neutral-500">
          Nessun {cat.singular.toLowerCase()} per questo cliente.
        </div>
      ) : filteredOwn.length === 0 ? (
        <div className="bru-card p-8 text-center text-neutral-500">
          Nessun risultato per "{search}".
        </div>
      ) : isDeviceKind ? (
        <div className="space-y-4">
          {subcats
            .filter((s) => (grouped[s.value] || []).length > 0)
            .map((s) => {
              const list = grouped[s.value] || [];
              const open = openCats.has(s.value);
              return (
                <div key={s.value} className="bru-card overflow-hidden" data-testid={`group-${s.value}`}>
                  <button
                    onClick={() => toggleCat(s.value)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-neutral-50 hover:bg-neutral-100 border-b border-neutral-200"
                    data-testid={`toggle-${s.value}`}
                  >
                    <div className="flex items-center gap-3">
                      <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
                      <span className="font-bold">{s.label}</span>
                      <span className="text-xs text-neutral-500">({list.length})</span>
                    </div>
                  </button>
                  {open && list.map((it) => <Row key={it.id} it={it} />)}
                </div>
              );
            })}
          {/* Any item with an unknown category falls into a fallback bucket */}
          {Object.entries(grouped)
            .filter(([k]) => !subcats.some((s) => s.value === k))
            .map(([k, list]) => (
              <div key={k} className="bru-card overflow-hidden">
                <div className="px-4 py-3 bg-neutral-50 border-b border-neutral-200 font-bold">
                  {labelByValue[k] || k} <span className="text-xs text-neutral-500 font-normal">({list.length})</span>
                </div>
                {list.map((it) => <Row key={it.id} it={it} />)}
              </div>
            ))}
        </div>
      ) : (
        <div className="bru-card overflow-hidden">
          {own.map((it) => <Row key={it.id} it={it} />)}
        </div>
      )}
    </>
  );
}

export default function CategoryListPage({ categoryKey }) {
  const cat = CATEGORY_BY_KEY[categoryKey];
  const { user, isAdmin } = useAuth();
  const { clientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogClientId, setDialogClientId] = useState(null);

  const itemsPath = cat.collection === "devices" ? "/devices" : "/scaffolds";
  const itemsKey = `${cat.collection}:${cat.kind}`;
  const { data: items = [], loading: itemsLoading, reload: reloadItems } = useCachedData(
    itemsKey,
    () => api.get(`${itemsPath}?kind=${cat.kind}`).then((r) => r.data || []),
  );
  const { data: clients = [], loading: clientsLoading } = useCachedData(
    "clients:all",
    () => api.get("/clients").then((r) => r.data || []).catch(() => []),
    { enabled: isAdmin },
  );
  const loading = itemsLoading || (isAdmin && clientsLoading);

  // Opening the dialog from the home ?new=1 query-string
  useEffect(() => {
    if (searchParams.get("new") === "1" && isAdmin) {
      setDialogOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, isAdmin, setSearchParams]);

  const openNew = (presetClient = null) => {
    setDialogClientId(presetClient);
    setDialogOpen(true);
  };

  // Any mutation (create/update/delete) must invalidate the related cache
  // keys so the next read is fresh. The homepage dashboard also depends on
  // these counts.
  const invalidateRelated = () => {
    invalidate(itemsKey);
    invalidate("dashboard:home");
  };

  // Client role: show items for own client(s) directly (skip the client-list step).
  const effectiveClientId = clientId || (!isAdmin ? (user?.client_ids?.[0] || user?.client_id) : null);

  const content = (
    effectiveClientId
      ? <ClientItemsView cat={cat} clientId={effectiveClientId} items={items} clients={clients} onNew={openNew} />
      : <ClientCards cat={cat} items={items} clients={clients} onNew={() => openNew()} />
  );

  const DeviceOrScaffoldDialog = cat.collection === "devices" ? DeviceDialog : ScaffoldDialog;

  // Show content immediately when cached data exists; only spin on cold load.
  const showSpinner = loading && items.length === 0;

  return (
    <main className="min-h-screen max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
      {showSpinner ? <div className="text-neutral-500">Caricamento…</div> : content}

      {isAdmin && (
        <DeviceOrScaffoldDialog
          open={dialogOpen}
          onClose={() => { setDialogOpen(false); setDialogClientId(null); }}
          onSaved={() => {
            setDialogOpen(false);
            setDialogClientId(null);
            invalidateRelated();
            reloadItems();
          }}
          initialClientId={dialogClientId || effectiveClientId}
          kind={cat.kind}
        />
      )}
    </main>
  );
}
